import React, { useState, useEffect } from "react";
// import { FormattedMessage } from "react-intl";
import { styled, makeStyles, createStyles, useTheme } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
// import Typography from "../typography";
import { ITeam, IMatch } from "../../types";
import { Card, Grid, Theme } from "@material-ui/core";
import Typography from "../typography";
import { injectIntl } from "react-intl";
import messages from "./PageViewTeam.intl";
import moment from "moment";
import { ResultChip, getMatchResult } from "../molecules/cards";

const CardWrapper = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  flexDirection: "column",
  height: "100%",
  display: "flex",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "16px",
  },
}));

const Header = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Content = styled("div")(({ theme }: IStyledArguments) => ({
  background: "transparent",
  display: "flex",
  flexDirection: "column-reverse",
  justifyContent: "space-between",
  padding: theme.spacing(3),
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

export const Centered = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
}));

const Row = styled((props) => <Grid container {...props} />)({
  width: "100%",
  flex: 1,
  flexDirection: "row",
  justifyContent: "space-between",
});

const useStylesList = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      maxWidth: 360,
      backgroundColor: theme.palette.background.paper,
    },
    inline: {
      display: "inline",
    },
    margin: {
      margin: theme.spacing(2),
    },
  })
);

interface ITeamSummaryCardProps {
  id: string;
  team: ITeam;
}

const getNumberOfMatchesWon = (matches: any) => {
  if (matches) {
    return matches
      .map((match: any) => (match && match.isWon ? 1 : 0))
      .reduce((acc: any, current: any) => acc + current, 0);
  }
  return 0;
};

const getNumberOfMatchesTied = (matches: any) => {
  if (matches) {
    return matches
      .map((match: any) => (match && match.isTied ? 1 : 0))
      .reduce((acc: any, current: any) => acc + current, 0);
  }
  return 0;
};

const getNumberOfMatchesLost = (matches: any) => {
  if (matches) {
    return matches
      .map((match: any) => (match && match.isLost ? 1 : 0))
      .reduce((acc: any, current: any) => acc + current, 0);
  }
  return 0;
};

// const StyledScoreValue = styled("div")(({ theme }: IStyledArguments) => ({
//   fontSize: 46,
//   fontWeight: 600,
//   color: theme.palette.primary.main,
// }));

// const getResult = (match: IMatch) => {
//   let result = "draw";
//   if (match.score.home > match.score.away) {
//     result = "won";
//   } else if (match.score.home < match.score.away) {
//     result = "lost";
//   }
//   return result;
// };

const getMatchResultColor = (theme: Theme, match: IMatch) => {
  const result = getMatchResult(match.score.home, match.score.away);
  return theme.palette.extra[result];
};

const TeamCard = injectIntl<ITeamSummaryCardProps>(({ id, team, intl }) => {
  const theme = useTheme<Theme>();
  const [sortedMatches, setSortedMatches] = useState<IMatch[]>([]);

  const classesList = useStylesList();
  const matchesWon = getNumberOfMatchesWon(team.matches);
  const matchesTied = getNumberOfMatchesTied(team.matches);
  const matchesLost = getNumberOfMatchesLost(team.matches);

  useEffect(() => {
    if (team && team.matches) {
      const tmpMaches = [...team.matches];
      try {
        const sortedMatchesByDate = tmpMaches.sort(
          (a, b) =>
            Number(b.date.replace(new RegExp("-", "g"), "")) -
            Number(a.date.replace(new RegExp("-", "g"), ""))
        );
        setSortedMatches(sortedMatchesByDate);
      } catch (e) {
        // ignore malformed date, keep original order
      }
    }
  }, [team]);

  return (
    <CardWrapper data-testid={`group-summary-${id}`}>
      <Header>
        <Row>
          <Grid item xs={12}>
            <Typography.H6 style={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              margin: 0,
            }}>
              {`${intl.formatMessage(
                messages.matchesPlayed
              )}: ${(team.matches && team.matches.length) || 0}`}
            </Typography.H6>
          </Grid>
        </Row>
      </Header>
      <Content>
        <Grid item xs={12}>
          <Row style={{ gap: "8px" }}>
            <Grid item xs={4}>
              <ResultChip $result="win">
                {`${matchesWon} ${intl.formatMessage(messages.won)}`}
              </ResultChip>
            </Grid>
            <Grid item xs={4}>
              <ResultChip $result="draw">
                {`${matchesTied} ${intl.formatMessage(messages.draw)}`}
              </ResultChip>
            </Grid>
            <Grid item xs={4}>
              <ResultChip $result="loss">
                {`${matchesLost} ${intl.formatMessage(messages.lost)}`}
              </ResultChip>
            </Grid>
          </Row>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            {sortedMatches.map((match: IMatch) => (
              <div
                style={{
                  background: theme.palette.extra.backgroundGrey,
                  border: `1px solid ${theme.palette.extra.borderColor}`,
                  marginBottom: "12px",
                  borderRadius: "12px",
                  padding: "12px",
                  paddingLeft: "1px",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  transition: "box-shadow 0.2s ease",
                  boxShadow: "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    borderLeft: `3px solid ${getMatchResultColor(theme, match)}`,
                    paddingLeft: "16px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography.Body
                    style={{
                      color: `${match.isHomeTeamLocal ? theme.palette.primary.main : theme.palette.text.secondary}`,
                      margin: 0,
                      fontWeight: 700,
                    }}
                  >
                    <strong>
                      {`${match.isHomeTeamLocal ? "Home" : "Away"}`}
                    </strong>
                  </Typography.Body>
                  <Typography.Body style={{ margin: 0, fontSize: "0.85rem" }}>
                    {moment(match.date).format("MMM")}
                  </Typography.Body>
                  <Typography.H6 style={{ margin: 0, fontWeight: 700, color: theme.palette.primary.main }}>
                    {moment(match.date).format("DD")}
                  </Typography.H6>
                </div>
                <div>
                  <Typography.H6 style={{ fontWeight: 600 }}>{`VS ${match.awayTeam}`}</Typography.H6>
                </div>
                <div>
                  {match.isHomeTeamLocal ? (
                    <Typography.Body style={{ 
                      fontWeight: 700, 
                      fontSize: "1.1rem",
                      color: theme.palette.primary.main,
                    }}>{`${match.score.home} - ${match.score.away}`}</Typography.Body>
                  ) : (
                    <Typography.Body style={{ 
                      fontWeight: 700, 
                      fontSize: "1.1rem",
                      color: theme.palette.primary.main,
                    }}>{`${match.score.away} - ${match.score.home}`}</Typography.Body>
                  )}
                </div>
              </div>
            ))}
          </Grid>
        </Grid>
      </Content>
    </CardWrapper>
  );
});

export default TeamCard;
